import React, { useState, useEffect, useCallback } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import ColorPickerPopover from '../components/ColorPickerPopover';
import { useAuth } from '../AuthContext';
import api from '../api';
import { toast } from 'sonner';
import { useWidgetData } from '../hooks/useWidgetData';

// ── Contrast utilities ──
function hexToLuminance(hex) {
  hex = (hex || '#000000').replace('#', '');
  if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
  if (hex.length !== 6) return 0;
  const r = parseInt(hex.substring(0,2),16)/255, g = parseInt(hex.substring(2,4),16)/255, b = parseInt(hex.substring(4,6),16)/255;
  const toLinear = c => c <= 0.03928 ? c/12.92 : Math.pow((c+0.055)/1.055, 2.4);
  return 0.2126*toLinear(r) + 0.7152*toLinear(g) + 0.0722*toLinear(b);
}
function contrastRatio(a, b) { const l1 = hexToLuminance(a), l2 = hexToLuminance(b); return (Math.max(l1,l2)+0.05)/(Math.min(l1,l2)+0.05); }
function autoTextColor(bgHex) { return hexToLuminance(bgHex || '#000') > 0.4 ? '#0F172A' : '#FFFFFF'; }

function makeDefaults(brand) {
  const t = brand?.details?.theme || {};
  const primary = t.primary || '#1E3A5F';
  const accent = t.cta || t.accent || t.secondary || '#FF7F22';
  const bName = brand?.title_legal || brand?.title || 'Your Agency';
  const bPhone = brand?.phone_internal || brand?.phone || '(xxx) xxx-xxxx';
  const defaultDisclaimer = brand?.details?.results_disclaimer ||
    `By clicking the button above and submitting your online term life insurance quote request to ${bName}, you are agreeing by your electronic signature to give ${bName} and its partners, your prior express written consent and continuing established business relationship permission to call you at each cell and residential phone number you provided in your online quote request, and any other subscriber or user of these phone numbers, using an automatic dialing system and pre-recorded and artificial voice messages any time from and after your inquiry to ${bName} for purposes of all federal and state telemarketing and Do-Not-Call laws and your prior affirmative written consent to email you at the email address(s) you provided in your online quote request, in each case to market our products and services to you and for all other purposes. Your consent is not required to get a quote or purchase anything from ${bName}, and you may instead reach us by phone at ${bPhone}.`;
  return {
    template: 'modern', header_style: 'colored', compact: true,
    primary_color: primary, accent_color: accent,
    eyebrow: 'GET A QUOTE', heading: '', subtitle: 'Real pricing. Top rated carriers.',
    cta_text: 'Continue', container_shadow: true,
    disclaimer: defaultDisclaimer,
    fields: {
      gender:   { active: true, label: 'Gender', order: 1 },
      age:      { active: true, label: 'Date of birth', order: 2 },
      smoker:   { active: true, label: 'Smoker status', order: 3 },
      coverage: { active: true, label: 'Coverage amount', order: 4 },
      term:     { active: true, label: 'Term length', order: 5 },
      health:   { active: true, label: 'Health class', order: 6 },
      name:     { active: true, label: 'Full name', order: 7 },
      email:    { active: true, label: 'Email address', order: 8 },
      phone:    { active: true, label: 'Phone number', order: 9 },
      type:     { active: false, label: 'Insurance type', order: 10, values: [] },
    },
  };
}

const ENGINE_NAMES = {
  '1':'Standard Term','2':'No Medical Exam','4':'Universal Life','5':'Return of Premium',
  '6':'Accidental Death','7':'Guaranteed Issue','9':'Final Expense','10':'Living Benefits',
  '12':'Instant Issue Life','13':"Children's Life",'14':'Agentless Life','17':'Whole Life','18':'Customer Choice',
};

const COVERAGE_OPTIONS = [25000,50000,75000,100000,150000,200000,250000,300000,400000,500000,750000,1000000,1500000,2000000];
function formatCurrency(n) { return '$' + Number(n).toLocaleString('en-US'); }

// ── Editor controls ──
function Toggle({ checked, onChange, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' }}>
      <button type="button" onClick={() => onChange(!checked)} style={{
        width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 150ms',
        background: checked ? '#2D87F0' : '#CBD5E1', flexShrink: 0,
      }}><span style={{ position: 'absolute', top: '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 150ms', left: checked ? '21px' : '3px', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} /></button>
      {label && <span style={{ fontSize: '13px', color: '#334155' }}>{label}</span>}
    </div>
  );
}

function Section({ title, icon, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', marginBottom: '10px' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer' }}>
        {icon && <i className={icon} style={{ fontSize: '13px', color: '#2D87F0', width: '18px', textAlign: 'center' }} />}
        <span style={{ flex: 1, fontWeight: '600', fontSize: '14px', color: '#0F172A', textAlign: 'left' }}>{title}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} style={{ color: '#94A3B8', fontSize: '11px' }} />
      </button>
      {open && <div style={{ padding: '0 16px 16px', borderTop: '1px solid #F1F5F9' }}>{children}</div>}
    </div>
  );
}

function FieldRow({ field, data, onChange, onMove, isFirst, isLast }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
      <button type="button" onClick={() => onChange({ ...data, active: !data.active })} style={{
        width: '38px', height: '20px', borderRadius: '10px', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 150ms', flexShrink: 0,
        background: data.active ? '#2D87F0' : '#CBD5E1',
      }}><span style={{ position: 'absolute', top: '2px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 150ms', left: data.active ? '20px' : '2px', boxShadow: '0 1px 2px rgba(0,0,0,.15)' }} /></button>
      <input className="input-field" value={data.label} onChange={e => onChange({ ...data, label: e.target.value })}
        style={{ flex: 1, height: '34px', fontSize: '13px', padding: '0 10px', opacity: data.active ? 1 : 0.4, border: '1px solid #E2E8F0', borderRadius: '8px' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <button type="button" disabled={isFirst} onClick={() => onMove(field, -1)} style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', padding: '2px 6px', opacity: isFirst ? 0.2 : 0.7 }}><i className="fas fa-chevron-up" style={{ fontSize: '10px', color: '#475569' }} /></button>
        <button type="button" disabled={isLast} onClick={() => onMove(field, 1)} style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', padding: '2px 6px', opacity: isLast ? 0.2 : 0.7 }}><i className="fas fa-chevron-down" style={{ fontSize: '10px', color: '#475569' }} /></button>
      </div>
    </div>
  );
}

// ── Floating error tooltip (no layout shift) ──
function FieldError({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '4px', zIndex: 20, pointerEvents: 'auto' }}>
      <div onClick={onDismiss} style={{
        background: '#DC2626', color: '#fff', fontSize: '11px', fontWeight: '600',
        padding: '5px 10px', borderRadius: '6px', whiteSpace: 'nowrap', cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(220,38,38,.3)', lineHeight: 1.3,
      }}>
        {message}
        <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #DC2626' }} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════
// INTERACTIVE WIDGET PREVIEW
// ════════════════════════════════════════════════════
function WidgetPreview({ d, sortedFields, engines, displayTitle, onContinue }) {
  const isModern = d.template === 'modern';
  const isElegant = d.template === 'elegant';
  const primary = d.primary_color || '#1E3A5F';
  const accent = d.accent_color || '#FF7F22';
  const textOnPrimary = autoTextColor(primary);
  const textOnAccent = autoTextColor(accent);
  const compact = d.compact;
  const lightHeader = d.header_style === 'light';

  // Interactive state
  const [vals, setVals] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const setVal = (k, v) => { setVals(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: '' })); };

  const handleSubmit = () => {
    if (onContinue) { onContinue(); return; }
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  // Phone formatting + validation
  const handlePhone = (raw) => {
    let digits = raw.replace(/\D/g, '').slice(0, 10);
    let fmt = '';
    if (digits.length > 0) fmt += '(' + digits.slice(0, 3);
    if (digits.length >= 3) fmt += ') ';
    if (digits.length > 3) fmt += digits.slice(3, 6);
    if (digits.length >= 6) fmt += '-';
    if (digits.length > 6) fmt += digits.slice(6, 10);
    setVal('phone', fmt);
  };
  const validatePhone = (val) => {
    const digits = (val || '').replace(/\D/g, '');
    if (digits.length > 0 && digits.length < 10) {
      setErrors(p => ({ ...p, phone: 'Phone number must be 10 digits' }));
    } else if (digits.length === 10) {
      const isSeq = '1234567890'.includes(digits) || '0987654321'.includes(digits);
      const isRepeated = /^(.)\1{9}$/.test(digits);
      if (isSeq || isRepeated) setErrors(p => ({ ...p, phone: 'Please enter a valid phone number' }));
    }
  };
  // Email validation
  const validateEmail = (val) => {
    if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val)) {
      setErrors(p => ({ ...p, email: 'Please enter a valid email address' }));
    }
  };

  // DOB
  const getDays = (m, y) => {
    if (!m) return 31;
    const mi = parseInt(m);
    if ([4,6,9,11].includes(mi)) return 30;
    if (mi === 2) { const yi = parseInt(y) || 2000; return (yi%4===0 && (yi%100!==0||yi%400===0)) ? 29 : 28; }
    return 31;
  };
  const dobMonth = vals.dob_month || '';
  const dobDay = vals.dob_day || '';
  const dobYear = vals.dob_year || '';
  const maxDays = getDays(dobMonth ? parseInt(dobMonth) : null, dobYear || null);

  // Coverage custom
  const handleCoverageCustom = (raw) => {
    const digits = raw.replace(/[^0-9]/g, '');
    setVal('coverage', digits ? '$' + Number(digits).toLocaleString('en-US') : '');
  };

  // Styles per template
  const rad = isModern || isElegant ? '12px' : '0';
  const inputH = compact ? '40px' : '44px';
  const inputStyle = {
    width: '100%', height: inputH,
    borderRadius: isModern || isElegant ? '12px' : '0',
    border: isModern ? 'none' : isElegant ? '1px solid #E2E8F0' : '2px solid #CBD5E1',
    background: isModern ? '#F1F5F9' : isElegant ? '#FAFBFC' : 'transparent',
    fontSize: compact ? '14px' : '15px', color: '#334155', padding: '0 14px', appearance: 'none', outline: 'none',
  };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };
  const labelStyle = isModern
    ? { fontSize: compact ? '13px' : '14px', fontWeight: '600', color: primary, display: 'block', marginBottom: compact ? '5px' : '8px', marginLeft: '2px' }
    : isElegant
    ? { fontSize: compact ? '12px' : '13px', fontWeight: '500', color: '#64748B', display: 'block', marginBottom: compact ? '5px' : '8px', letterSpacing: '0.3px' }
    : { fontSize: compact ? '12px' : '13px', fontWeight: '800', color: '#0F172A', display: 'block', marginBottom: compact ? '5px' : '7px', textTransform: 'uppercase', letterSpacing: '0.5px' };

  const activeFields = sortedFields.filter(([, f]) => f.active);

  const renderField = ([key, data]) => {
    const mb = compact ? '14px' : '20px';
    if (key === 'gender' || key === 'smoker') {
      const opts = key === 'gender' ? ['Male','Female'] : ['No','Yes'];
      const cur = vals[key] || opts[0];
      const toggleFontSize = compact ? '14px' : '15px';
      const togglePad = compact ? '10px' : '12px';
      return (
        <div key={key} style={{ marginBottom: mb }}>
          <label style={labelStyle}>{data.label}</label>
          {isModern || isElegant ? (
            <div style={{ display: 'flex', padding: '3px', background: isElegant ? '#F1F5F9' : '#F1F5F9', borderRadius: '12px', gap: '3px' }}>
              {opts.map(o => (
                <div key={o} onClick={() => setVal(key, o)} style={{
                  flex: 1, textAlign: 'center', padding: togglePad, borderRadius: '10px', fontSize: toggleFontSize, fontWeight: '600', cursor: 'pointer', transition: 'all 150ms',
                  background: cur === o ? (isElegant ? primary : '#fff') : 'transparent',
                  color: cur === o ? (isElegant ? autoTextColor(primary) : primary) : '#94A3B8',
                  boxShadow: cur === o ? (isElegant ? 'none' : '0 1px 3px rgba(0,0,0,.08)') : 'none',
                }}>{o}</div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', border: '2px solid #CBD5E1' }}>
              {opts.map((o, i) => (
                <div key={o} onClick={() => setVal(key, o)} style={{
                  flex: 1, textAlign: 'center', padding: togglePad, fontSize: toggleFontSize, fontWeight: '700', cursor: 'pointer',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  background: cur === o ? primary : 'transparent', color: cur === o ? textOnPrimary : '#94A3B8',
                  borderRight: i === 0 ? '2px solid #CBD5E1' : 'none',
                }}>{o}</div>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (key === 'age') {
      const bRad = isModern || isElegant ? '12px' : '0';
      const dobBg = inputStyle.background;
      const dobBorder = isModern ? 'none' : inputStyle.border;
      const dobErr = errors.dob_month || errors.dob_day || errors.dob_year || '';
      const fieldBase = { height: inputH, fontSize: inputStyle.fontSize, color: '#334155', textAlign: 'center', padding: '0 4px', appearance: 'none', outline: 'none', border: 'none', background: 'transparent', fontVariantNumeric: 'tabular-nums', width: '100%' };
      const sepStyle = { fontSize: '16px', color: '#B0BEC5', fontWeight: '300', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '16px', flexShrink: 0, background: 'transparent' };
      return (
        <div key={key} style={{ marginBottom: mb }}>
          <label style={labelStyle}>{data.label}</label>
          <div style={{ position: 'relative' }}>
            <div style={{
              display: 'flex', alignItems: 'center', height: inputH,
              borderRadius: bRad, background: dobBg,
              border: dobErr ? (isModern ? '2px solid #FCA5A5' : '2px solid #FCA5A5') : dobBorder, overflow: 'hidden',
            }}>
              <input type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={2}
                style={{ ...fieldBase, flex: '1 1 0' }}
                value={dobMonth} placeholder="MM"
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                setVal('dob_month', v);
                setErrors(p => ({ ...p, dob_month: '', dob_day: '' }));
                if (v.length === 2) e.target.nextElementSibling?.nextElementSibling?.focus();
              }}
              onBlur={e => {
                const v = e.target.value;
                if (v && (v.length < 2 || parseInt(v) < 1 || parseInt(v) > 12))
                  setErrors(p => ({ ...p, dob_month: 'Month must be 01-12' }));
                if (v && v.length === 2 && dobDay && dobDay.length === 2) {
                  const maxD = getDays(parseInt(v), dobYear ? parseInt(dobYear) : null);
                  if (parseInt(dobDay) > maxD)
                    setErrors(p => ({ ...p, dob_day: `Only ${maxD} days in this month` }));
                }
              }}
            />
            <span style={sepStyle}>/</span>
            <input type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={2}
              style={{ ...fieldBase, flex: '1 1 0' }}
              value={dobDay} placeholder="DD"
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                setVal('dob_day', v);
                setErrors(p => ({ ...p, dob_day: '' }));
                if (v.length === 2) e.target.nextElementSibling?.nextElementSibling?.focus();
              }}
              onBlur={e => {
                const v = e.target.value;
                if (v && v.length === 2) {
                  const d = parseInt(v);
                  if (d < 1) { setErrors(p => ({ ...p, dob_day: 'Day must be at least 01' })); return; }
                  const maxD = getDays(dobMonth ? parseInt(dobMonth) : null, dobYear ? parseInt(dobYear) : null);
                  if (d > maxD) setErrors(p => ({ ...p, dob_day: `Max ${maxD} days for this month` }));
                } else if (v && v.length < 2) {
                  setErrors(p => ({ ...p, dob_day: 'Enter 2 digits (01-31)' }));
                }
              }}
            />
            <span style={sepStyle}>/</span>
            <input type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={4}
              style={{ ...fieldBase, flex: '1.4 1 0' }}
              value={dobYear} placeholder="YYYY"
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                setVal('dob_year', v);
                setErrors(p => ({ ...p, dob_year: '' }));
              }}
              onBlur={e => {
                const v = e.target.value;
                if (v && v.length === 4) {
                  const y = parseInt(v);
                  const thisYear = new Date().getFullYear();
                  if (y < 1920) setErrors(p => ({ ...p, dob_year: 'Year must be 1920 or later' }));
                  else if (y > thisYear) setErrors(p => ({ ...p, dob_year: `Year cannot exceed ${thisYear}` }));
                  if (dobMonth && dobDay) {
                    const maxD = getDays(parseInt(dobMonth), y);
                    if (parseInt(dobDay) > maxD) setErrors(p => ({ ...p, dob_day: `Max ${maxD} days for this month` }));
                  }
                } else if (v && v.length < 4) {
                  setErrors(p => ({ ...p, dob_year: 'Enter 4-digit year' }));
                }
              }}
            />
          </div>
          <FieldError message={dobErr} onDismiss={() => setErrors(p => ({ ...p, dob_month: '', dob_day: '', dob_year: '' }))} />
          </div>
        </div>
      );
    }
    if (key === 'coverage') {
      const sel = vals.coverage_sel || '';
      return (
        <div key={key} style={{ marginBottom: mb }}>
          <label style={labelStyle}>{data.label}</label>
          <select style={selectStyle} value={sel} onChange={e => { setVal('coverage_sel', e.target.value); if (e.target.value !== 'custom') setVal('coverage', ''); }}>
            <option value="">Select amount</option>
            {COVERAGE_OPTIONS.map(a => <option key={a} value={a}>{formatCurrency(a)}</option>)}
            <option value="custom">Custom amount...</option>
          </select>
          {sel === 'custom' && <input type="text" value={vals.coverage || ''} onChange={e => handleCoverageCustom(e.target.value)} placeholder="$0"
            style={{ ...inputStyle, marginTop: '6px', background: isModern ? '#fff' : 'transparent', border: isModern ? '1px solid #E2E8F0' : '2px solid #CBD5E1' }} />}
        </div>
      );
    }
    if (key === 'term') {
      return (
        <div key={key} style={{ marginBottom: mb }}>
          <label style={labelStyle}>{data.label}</label>
          <select style={selectStyle} value={vals.term || ''} onChange={e => setVal('term', e.target.value)}>
            <option value="">Select term</option>
            {[10,15,20,25,30].map(t => <option key={t} value={t}>{t} years</option>)}
          </select>
        </div>
      );
    }
    if (key === 'health') {
      return (
        <div key={key} style={{ marginBottom: mb }}>
          <label style={labelStyle}>{data.label}</label>
          <select style={selectStyle} value={vals.health || ''} onChange={e => setVal('health', e.target.value)}>
            <option value="">Select health class</option>
            {['Excellent','Very Good','Good','Average','Below Average'].map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
      );
    }
    if (key === 'type') {
      return (
        <div key={key} style={{ marginBottom: mb }}>
          <label style={labelStyle}>{data.label}</label>
          <select style={selectStyle} value={vals.type || ''} onChange={e => setVal('type', e.target.value)}>
            <option value="">Select type</option>
            {Object.entries(engines).map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select>
        </div>
      );
    }
    if (key === 'name') {
      return (
        <div key={key} style={{ marginBottom: mb }}>
          <label style={labelStyle}>{data.label}</label>
          <input type="text" style={inputStyle} value={vals.name || ''} onChange={e => setVal('name', e.target.value)} placeholder="John Doe" />
        </div>
      );
    }
    if (key === 'email') {
      return (
        <div key={key} style={{ marginBottom: mb, position: 'relative' }}>
          <label style={labelStyle}>{data.label}</label>
          <input type="email" style={{ ...inputStyle, border: errors.email ? '2px solid #FCA5A5' : inputStyle.border }} value={vals.email || ''} onChange={e => setVal('email', e.target.value)} onBlur={e => validateEmail(e.target.value)} placeholder="john@example.com" />
          <FieldError message={errors.email} onDismiss={() => setErrors(p => ({ ...p, email: '' }))} />
        </div>
      );
    }
    if (key === 'phone') {
      return (
        <div key={key} style={{ marginBottom: mb, position: 'relative' }}>
          <label style={labelStyle}>{data.label}</label>
          <input type="tel" style={{ ...inputStyle, border: errors.phone ? '2px solid #FCA5A5' : inputStyle.border }} value={vals.phone || ''} onChange={e => handlePhone(e.target.value)} onBlur={e => validatePhone(e.target.value)} placeholder="(555) 123-4567" />
          <FieldError message={errors.phone} onDismiss={() => setErrors(p => ({ ...p, phone: '' }))} />
        </div>
      );
    }
    return (
      <div key={key} style={{ marginBottom: mb }}>
        <label style={labelStyle}>{data.label}</label>
        <div style={{ height: inputStyle.height, borderRadius: rad, background: isElegant ? '#FAFBFC' : '#F1F5F9', border: isModern ? 'none' : isElegant ? '1px solid #E2E8F0' : '2px solid #CBD5E1' }} />
      </div>
    );
  };

  // Container styles
  const maxW = compact ? '520px' : '420px';
  const containerStyle = isModern
    ? { borderRadius: '24px', overflow: 'hidden', background: '#fff', maxWidth: maxW, width: '100%',
        boxShadow: d.container_shadow ? '0 20px 60px -12px rgba(0,0,0,.12), 0 0 0 1px rgba(0,0,0,.03)' : '0 0 0 1px rgba(0,0,0,.06)' }
    : isElegant
    ? { borderRadius: '20px', overflow: 'hidden', background: '#fff', maxWidth: maxW, width: '100%',
        border: `1px solid #E2E8F0`,
        boxShadow: d.container_shadow ? '0 12px 40px -8px rgba(0,0,0,.08), 0 4px 12px -4px rgba(0,0,0,.04)' : '0 0 0 1px rgba(0,0,0,.04)' }
    : { overflow: 'hidden', background: '#fff', maxWidth: maxW, width: '100%',
        border: `3px solid ${primary}`, boxShadow: d.container_shadow ? `8px 8px 0px 0px ${primary}` : 'none' };

  const headerBg = lightHeader ? '#fff' : primary;
  const headerText = lightHeader ? primary : textOnPrimary;
  const headerPad = compact ? '20px 28px' : (isElegant ? '32px 36px' : isModern ? '28px 32px' : '24px 28px');
  const bodyPad = compact ? '20px 28px 22px' : (isElegant ? '28px 36px 28px' : isModern ? '28px 28px 24px' : '24px 28px 20px');

  const renderHeader = () => {
    if (isElegant) {
      return (
        <div style={{ background: lightHeader ? '#fff' : primary, padding: headerPad, textAlign: 'center',
          borderBottom: lightHeader ? '1px solid #F1F5F9' : 'none' }}>
          {d.eyebrow && <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '3px', color: lightHeader ? accent : `${textOnPrimary}99`, marginBottom: '10px', textTransform: 'uppercase' }}>{d.eyebrow}</div>}
          <div style={{ fontSize: compact ? '20px' : '24px', fontWeight: '700', color: lightHeader ? '#0F172A' : textOnPrimary, lineHeight: 1.2, letterSpacing: '-0.3px' }}>{displayTitle}</div>
          {d.subtitle && <div style={{ fontSize: '14px', color: lightHeader ? '#94A3B8' : `${textOnPrimary}AA`, marginTop: '8px', lineHeight: 1.4 }}>{d.subtitle}</div>}
        </div>
      );
    }
    return (
      <div style={{ background: headerBg, padding: headerPad, textAlign: isModern ? 'center' : 'left',
        borderBottom: lightHeader ? `2px solid ${isModern ? '#F1F5F9' : primary}` : 'none' }}>
        {d.eyebrow && (
          isModern
            ? <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2.5px', color: lightHeader ? '#94A3B8' : `${headerText}B3`, marginBottom: '8px', textTransform: 'uppercase' }}>{d.eyebrow}</div>
            : <div style={{ display: 'inline-block', padding: '3px 10px', background: lightHeader ? `${primary}10` : 'rgba(255,255,255,.15)', fontSize: '11px', fontWeight: '800', letterSpacing: '2px', color: headerText, textTransform: 'uppercase', marginBottom: '10px' }}>{d.eyebrow}</div>
        )}
        <div style={{ fontSize: compact ? '20px' : (isModern ? '24px' : '28px'), fontWeight: isModern ? '700' : '900', color: headerText, lineHeight: 1.2, letterSpacing: isModern ? 'normal' : '-0.5px' }}>{displayTitle}</div>
        {d.subtitle && <div style={{ fontSize: '14px', color: lightHeader ? '#64748B' : `${headerText}B3`, marginTop: '8px', lineHeight: 1.4, fontWeight: isModern ? '400' : '500' }}>{d.subtitle}</div>}
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      {renderHeader()}

      {/* Body */}
      <div style={{ padding: bodyPad }}>
        {compact ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {activeFields.map(renderField)}
          </div>
        ) : (
          activeFields.map(renderField)
        )}

        {/* CTA */}
        <button type="button" onClick={handleSubmit} data-testid="widget-preview-cta" style={{
          width: '100%', height: compact ? '48px' : (isElegant ? '52px' : isModern ? '50px' : '52px'),
          borderRadius: isModern || isElegant ? '14px' : '0', border: 'none',
          fontSize: compact ? '15px' : '16px',
          fontWeight: isModern ? '700' : isElegant ? '600' : '900', marginTop: compact ? '6px' : '10px',
          background: submitted ? '#059669' : accent, color: submitted ? '#fff' : textOnAccent,
          boxShadow: isModern || isElegant ? `0 4px 14px ${accent}40` : 'none',
          textTransform: isElegant ? 'none' : (isModern ? 'none' : 'uppercase'), letterSpacing: isModern || isElegant ? 'normal' : '1px',
          cursor: 'pointer', transition: 'all 200ms',
        }}>
          {submitted ? (<><i className="fas fa-check" style={{ marginRight: '6px' }} />Submitted!</>) : (d.cta_text || 'Continue')}
        </button>

        {/* Disclaimer - scrollable */}
        {d.disclaimer && (
          <div style={{ maxHeight: '80px', overflowY: 'auto', fontSize: '10px', color: '#94A3B8', lineHeight: 1.5, marginTop: compact ? '10px' : '14px', padding: '8px 10px', background: '#F8FAFC', borderRadius: isModern ? '8px' : '0', border: '1px solid #F1F5F9' }}>
            {d.disclaimer}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════
export default function WidgetsPage() {
  const { brand } = useAuth();
  const widgetData = useWidgetData();
  const { widgets, setWidgets, loading, error, fetchWidgets, isIframe } = widgetData;
  const [engines, setEngines] = useState(ENGINE_NAMES);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newEngine, setNewEngine] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [iframeSrc, setIframeSrc] = useState(null);

  useEffect(() => {
    fetchWidgets();
    // Only query the local backend for engines when NOT embedded in an iframe.
    // In iframe mode, use the static ENGINE_NAMES fallback (the map is deterministic).
    if (!isIframe) {
      api.get('/widgets').then(r => { if (r.data.engines && Object.keys(r.data.engines).length) setEngines(r.data.engines); }).catch(() => {});
    }
  }, []);

  const openEditor = (widget) => {
    const defaults = makeDefaults(brand);
    const details = { ...defaults, ...widget.details };
    if (widget.details?.fields) {
      details.fields = { ...defaults.fields };
      Object.keys(widget.details.fields).forEach(k => {
        if (defaults.fields[k]) details.fields[k] = { ...defaults.fields[k], ...widget.details.fields[k] };
      });
    }
    setForm({ title: widget.title, engine_id: widget.engine_id, details });
    setEditing(widget); setDirty(false); setIframeSrc(null);
  };

  const closeEditor = () => {
    if (dirty && !window.confirm('You have unsaved changes. Discard?')) return;
    setEditing(null); setDirty(false); setIframeSrc(null);
  };

  const saveWidget = async () => {
    setSaving(true);
    try {
      const updated = await widgetData.saveWidget(editing, { title: form.title, engine_id: form.engine_id, details: form.details });
      setWidgets(prev => {
        const exists = prev.find(w => (w.key && w.key === updated.key) || (w.id && w.id === updated.id));
        if (exists) return prev.map(w => ((w.key && w.key === updated.key) || (w.id && w.id === updated.id)) ? updated : w);
        return [updated, ...prev];
      });
      setEditing(updated); openEditor(updated);
      toast.success('Widget saved!');
    } catch (e) { toast.error('Error saving widget.'); }
    finally { setSaving(false); }
  };

  const updateForm = useCallback((path, value) => {
    setForm(prev => {
      const next = { ...prev };
      if (path === 'title') { next.title = value; }
      else if (path === 'engine_id') { next.engine_id = value; }
      else {
        const keys = path.split('.');
        let obj = next.details = { ...next.details };
        for (let i = 0; i < keys.length - 1; i++) { obj[keys[i]] = { ...obj[keys[i]] }; obj = obj[keys[i]]; }
        obj[keys[keys.length - 1]] = value;
      }
      return next;
    });
    setDirty(true);
  }, []);

  const moveField = useCallback((fieldKey, dir) => {
    setForm(prev => {
      const fields = { ...prev.details.fields };
      const sorted = Object.entries(fields).sort((a,b) => a[1].order - b[1].order);
      const idx = sorted.findIndex(([k]) => k === fieldKey);
      if ((dir===-1 && idx===0) || (dir===1 && idx===sorted.length-1)) return prev;
      const swapIdx = idx + dir;
      const tmp = sorted[idx][1].order;
      fields[sorted[idx][0]] = { ...sorted[idx][1], order: sorted[swapIdx][1].order };
      fields[sorted[swapIdx][0]] = { ...sorted[swapIdx][1], order: tmp };
      return { ...prev, details: { ...prev.details, fields } };
    });
    setDirty(true);
  }, []);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const created = await widgetData.createWidget(newTitle, newEngine || null);
      setWidgets(prev => [created, ...prev]);
      setShowCreate(false); setNewTitle(''); setNewEngine('');
      toast.success('Widget created!');
      openEditor(created);
    } catch (e) { toast.error('Error creating widget.'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (widgetId) => {
    if (!window.confirm('Delete this widget? This cannot be undone.')) return;
    try {
      if (!isIframe) { await api.delete(`/widgets/${widgetId}`); }
      setWidgets(prev => prev.filter(w => w.id !== widgetId && w.key !== widgetId));
      if (editing?.id === widgetId || editing?.key === widgetId) { setEditing(null); setDirty(false); }
      toast.success('Widget deleted.');
    } catch (e) { toast.error('Error deleting widget.'); }
  };

  const getDomain = () => brand?.custom_domain || brand?.quoter_domain || `${brand?.subdomain_alias || brand?.subdomain || 'demo'}.quoteplicity.com`;
  const handleCopy = (widget) => {
    navigator.clipboard.writeText(`<script src="https://${getDomain()}/qp-widget/${widget.key}" async></script>`).catch(() => {});
    setCopiedId(widget.id); setTimeout(() => setCopiedId(null), 3000);
    toast.success('Embed code copied!');
  };

  // ═══ EDITOR ═══
  if (editing) {
    const d = form.details || {};
    const sortedFields = Object.entries(d.fields || {}).sort((a,b) => a[1].order - b[1].order);
    const displayTitle = d.heading || engines[form.engine_id] || 'Life Insurance';
    const primary = d.primary_color || '#1E3A5F';
    const accent = d.accent_color || '#FF7F22';
    const ctaRatio = contrastRatio(accent, autoTextColor(accent) === '#fff' ? '#FFFFFF' : '#0F172A');

    const TemplateBtn = ({ id, label, icon, desc }) => {
      const active = d.template === id;
      return (
        <button type="button" onClick={() => updateForm('template', id)} data-testid={`template-${id}`}
          style={{ flex: 1, padding: '14px 12px', borderRadius: '12px', cursor: 'pointer', textAlign: 'center', transition: 'all 150ms', border: active ? `2px solid ${primary}` : '2px dashed #CBD5E1', background: active ? `${primary}08` : '#fff' }}>
          <i className={icon} style={{ fontSize: '20px', color: active ? primary : '#94A3B8', marginBottom: '6px', display: 'block' }} />
          <div style={{ fontSize: '13px', fontWeight: '700', color: active ? primary : '#334155' }}>{label}</div>
          <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>{desc}</div>
        </button>
      );
    };

    const HeaderStyleBtn = ({ id, label }) => {
      const active = d.header_style === id;
      return (
        <button type="button" onClick={() => updateForm('header_style', id)}
          style={{ flex: 1, padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', transition: 'all 150ms',
            border: active ? `2px solid ${primary}` : '1px solid #E2E8F0', background: active ? `${primary}08` : '#fff', color: active ? primary : '#64748B' }}>
          {label}
        </button>
      );
    };

    // Build funnel URL from widget form values
    const buildFunnelUrl = () => {
      const domain = getDomain();
      const eid = form.engine_id || '1';
      return `https://${domain}/term-life-insurance?engine_id=${eid}`;
    };

    return (
      <ProtectedLayout>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px 16px' }}>
          {/* Top bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <button type="button" onClick={closeEditor} data-testid="widget-editor-back"
              style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E2E8F0', borderRadius: '8px', background: '#fff', cursor: 'pointer' }}>
              <i className="fas fa-arrow-left" style={{ fontSize: '13px', color: '#334155' }} />
            </button>
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: '18px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Edit Widget</h1>
              <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>{form.title}</div>
            </div>
            <button type="button" onClick={saveWidget} disabled={saving || !dirty} data-testid="widget-save-button"
              style={{ height: '34px', padding: '0 18px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: dirty ? 'pointer' : 'default', background: dirty ? '#2D87F0' : '#E2E8F0', color: dirty ? '#fff' : '#94A3B8', transition: 'all 150ms', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <i className={`fas ${saving ? 'fa-spinner fa-spin' : dirty ? 'fa-cloud-arrow-up' : 'fa-check'}`} style={{ fontSize: '10px' }} />
              {saving ? 'Saving...' : dirty ? 'Save Changes' : 'Saved'}
            </button>
            <button type="button" onClick={() => handleCopy(editing)} data-testid="widget-copy-embed"
              style={{ height: '34px', padding: '0 14px', borderRadius: '8px', border: 'none', background: copiedId === editing.id ? '#059669' : '#475569', fontSize: '12px', fontWeight: '600', color: '#fff', cursor: 'pointer', transition: 'background 150ms', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <i className={`fas ${copiedId === editing.id ? 'fa-check' : 'fa-code'}`} style={{ fontSize: '10px' }} />
              {copiedId === editing.id ? 'Copied!' : 'Embed'}
            </button>
            <button type="button" onClick={() => handleDelete(editing.id)}
              style={{ height: '34px', width: '34px', borderRadius: '8px', border: '1px solid #FCA5A5', background: '#FEF2F2', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-trash" style={{ color: '#DC2626', fontSize: '12px' }} />
            </button>
          </div>

          {/* DESIGN CONTROLS ON TOP — 2 columns */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div>
              <Section title="Design" icon="fas fa-swatchbook" defaultOpen={true}>
                <div style={{ display: 'flex', gap: '8px', padding: '8px 0' }}>
                  <TemplateBtn id="modern" label="Modern" icon="fas fa-circle" desc="Soft & rounded" />
                  <TemplateBtn id="elegant" label="Elegant" icon="fas fa-gem" desc="Clean & refined" />
                  <TemplateBtn id="bold" label="Bold" icon="fas fa-square" desc="Sharp & striking" />
                </div>
                <div style={{ padding: '8px 0' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Header Style</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <HeaderStyleBtn id="colored" label="Colored" />
                    <HeaderStyleBtn id="light" label="Light / White" />
                  </div>
                </div>
                <div style={{ padding: '8px 0' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '8px' }}>Brand Color</label>
                  <ColorPickerPopover value={primary} onApply={v => updateForm('primary_color', v)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '4px 0' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', border: '2px solid #E2E8F0', background: primary }} />
                      <div><div style={{ fontSize: '12px', fontWeight: '500', color: '#334155' }}>Header & accents</div><div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#94A3B8' }}>{primary}</div></div>
                    </div>
                  </ColorPickerPopover>
                </div>
                <div style={{ padding: '4px 0' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '8px' }}>CTA Button</label>
                  <ColorPickerPopover value={accent} onApply={v => updateForm('accent_color', v)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '4px 0' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', border: '2px solid #E2E8F0', background: accent }} />
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '500', color: '#334155' }}>Call to action</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '11px', fontFamily: 'monospace', color: '#94A3B8' }}>{accent}</span>
                          {ctaRatio < 3 && <span style={{ fontSize: '10px', fontWeight: '600', color: '#DC2626' }}><i className="fas fa-triangle-exclamation" style={{ fontSize: '9px', marginRight: '3px' }} />Low contrast</span>}
                        </div>
                      </div>
                    </div>
                  </ColorPickerPopover>
                </div>
                <Toggle checked={d.container_shadow} onChange={v => updateForm('container_shadow', v)} label="Drop shadow" />
                <Toggle checked={d.compact} onChange={v => updateForm('compact', v)} label="Compact layout (2-column)" />
              </Section>

              <Section title="Disclaimer" icon="fas fa-shield-halved">
                <div style={{ padding: '8px 0' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Disclaimer text</label>
                  <textarea className="input-field" value={d.disclaimer || ''} onChange={e => updateForm('disclaimer', e.target.value)}
                    placeholder="By clicking Continue, you agree to our Privacy Policy..."
                    style={{ height: 'auto', minHeight: '80px', resize: 'vertical', fontSize: '12px', borderRadius: '8px', border: '1px solid #E2E8F0', padding: '8px 10px', lineHeight: '1.5' }} />
                </div>
              </Section>
            </div>

            <div>
              <Section title="General" icon="fas fa-cog" defaultOpen={true}>
                <div style={{ padding: '8px 0' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '5px' }}>Widget Name</label>
                  <input className="input-field" value={form.title || ''} onChange={e => updateForm('title', e.target.value)} placeholder="e.g. Homepage Widget" style={{ height: '36px', borderRadius: '8px', border: '1px solid #E2E8F0' }} />
                </div>
                <div style={{ padding: '8px 0' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '5px' }}>Quote Engine</label>
                  <select className="input-field" value={form.engine_id || ''} onChange={e => updateForm('engine_id', e.target.value)} style={{ height: '36px', borderRadius: '8px', border: '1px solid #E2E8F0', appearance: 'none', cursor: 'pointer' }}>
                    {Object.entries(engines).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </select>
                </div>
              </Section>

              <Section title="Text Labels" icon="fas fa-font">
                {[['Eyebrow','eyebrow','GET A QUOTE'],['Title','heading','Term Life Insurance'],['Subtitle','subtitle','Real pricing. Top rated carriers.'],['CTA Button','cta_text','Continue']].map(([label,key,ph]) => (
                  <div key={key} style={{ padding: '6px 0' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>{label}</label>
                    <input className="input-field" value={d[key] || ''} onChange={e => updateForm(key, e.target.value)} placeholder={ph} style={{ height: '34px', fontSize: '13px', borderRadius: '8px', border: '1px solid #E2E8F0' }} />
                  </div>
                ))}
              </Section>

              <Section title="Form Fields" icon="fas fa-list-check">
                {sortedFields.map(([key, data], idx) => (
                  <FieldRow key={key} field={key} data={data} onChange={v => updateForm(`fields.${key}`, v)} onMove={moveField} isFirst={idx===0} isLast={idx===sortedFields.length-1} />
                ))}
              </Section>
            </div>
          </div>

          {/* PREVIEW BELOW — full width */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px' }}>
                {iframeSrc ? 'Live Funnel Preview' : 'Widget Preview'}
              </div>
              {iframeSrc && (
                <button type="button" onClick={() => setIframeSrc(null)} data-testid="widget-back-to-widget"
                  style={{ height: '30px', padding: '0 12px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#fff', fontSize: '11px', fontWeight: '600', color: '#475569', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <i className="fas fa-arrow-left" style={{ fontSize: '9px' }} /> Back to Widget
                </button>
              )}
            </div>
            <div style={{
              borderRadius: '16px', overflow: 'hidden', border: '1px solid #E2E8F0',
              background: iframeSrc ? '#fff' : 'repeating-conic-gradient(#F8FAFC 0% 25%, #fff 0% 50%) 50% / 20px 20px',
              minHeight: iframeSrc ? '600px' : 'auto',
            }}>
              {iframeSrc ? (
                <iframe src={iframeSrc} title="Funnel Preview" data-testid="widget-funnel-iframe"
                  style={{ width: '100%', height: '700px', border: 'none' }}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                />
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '28px' }}>
                  <WidgetPreview d={d} sortedFields={sortedFields} engines={engines} displayTitle={displayTitle} onContinue={() => setIframeSrc(buildFunnelUrl())} />
                </div>
              )}
            </div>
          </div>
        </div>
      </ProtectedLayout>
    );
  }

  // ═══ LIST VIEW ═══
  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Widgets</h1>
            <p style={{ fontSize: '13px', color: '#64748B', margin: '4px 0 0' }}>Create and customize embeddable quote widgets for your website.</p>
          </div>
          <button className="btn-primary" onClick={() => setShowCreate(true)} data-testid="widget-create-button" style={{ padding: '8px 20px' }}>
            <i className="fas fa-plus" style={{ marginRight: '6px' }} />New Widget
          </button>
        </div>

        {showCreate && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowCreate(false)}>
            <div className="portal-card" style={{ width: '420px', padding: '28px' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                <h2 style={{ fontWeight: '700', fontSize: '17px', margin: 0, color: '#0F172A' }}>New Widget</h2>
                <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94A3B8' }}><i className="fas fa-times" /></button>
              </div>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#334155' }}>Widget Name</label>
                <input className="input-field" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. Homepage — Term" autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && newTitle.trim()) handleCreate(); }} style={{ borderRadius: '8px', border: '1px solid #E2E8F0' }} />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#334155' }}>Quote Engine</label>
                <select className="input-field" value={newEngine} onChange={e => setNewEngine(e.target.value)} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', appearance: 'none', cursor: 'pointer' }}>
                  <option value="">Select engine...</option>
                  {Object.entries(engines).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={() => setShowCreate(false)} style={{ height: '38px', padding: '0 18px', borderRadius: '8px' }}>Cancel</button>
                <button className="btn-primary" onClick={handleCreate} disabled={creating || !newTitle.trim()} style={{ height: '38px', padding: '0 22px', borderRadius: '8px' }}>
                  {creating ? 'Creating...' : 'Create Widget'}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ display: 'grid', gap: '10px' }}>{[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: '68px', borderRadius: '12px' }} />)}</div>
        ) : error ? (
          <div data-testid="widgets-error-state" className="portal-card" style={{ maxWidth: '520px', margin: '48px auto', padding: '40px 32px', textAlign: 'center', border: '1px solid #FCA5A5', background: '#FEF2F2' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <i className="fas fa-triangle-exclamation" style={{ fontSize: '24px', color: '#DC2626' }} />
            </div>
            <div style={{ fontWeight: '700', fontSize: '17px', marginBottom: '8px', color: '#0F172A' }}>Unable to load widgets</div>
            <div style={{ color: '#64748B', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>{error}</div>
            <button data-testid="widgets-error-retry" className="btn-primary" onClick={fetchWidgets} style={{ padding: '10px 28px', borderRadius: '10px' }}>
              <i className="fas fa-rotate-right" style={{ marginRight: '6px', fontSize: '12px' }} />Retry
            </button>
          </div>
        ) : widgets.length === 0 ? (
          <div className="portal-card" style={{ maxWidth: '480px', margin: '48px auto', padding: '48px 32px', textAlign: 'center' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <i className="fas fa-puzzle-piece" style={{ fontSize: '24px', color: '#2D87F0' }} />
            </div>
            <div style={{ fontWeight: '700', fontSize: '17px', marginBottom: '8px', color: '#0F172A' }}>No widgets yet</div>
            <div style={{ color: '#64748B', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>Create your first embeddable quote widget to start capturing leads from your website.</div>
            <button className="btn-primary" onClick={() => setShowCreate(true)} style={{ padding: '10px 28px', borderRadius: '10px' }}>Create Widget</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {widgets.map(widget => (
              <div key={widget.id} data-testid="widget-card" onClick={() => openEditor(widget)}
                style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 18px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', cursor: 'pointer', transition: 'border-color 150ms, box-shadow 150ms' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#2D87F0'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(45,135,240,.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="fas fa-puzzle-piece" style={{ color: '#2D87F0', fontSize: '16px' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#0F172A' }}>{widget.title}</div>
                  <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>{widget.engine_title || engines[widget.engine_id] || '—'}</div>
                </div>
                <div style={{ fontSize: '11px', color: '#CBD5E1', fontFamily: 'monospace', marginRight: '4px' }}>{widget.key}</div>
                <div style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
                  <button data-testid="widget-copy-button" onClick={() => handleCopy(widget)}
                    style={{ height: '32px', padding: '0 12px', borderRadius: '8px', border: '1px solid #E2E8F0', background: '#fff', fontSize: '12px', fontWeight: '600', color: '#475569', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <i className={`fas ${copiedId === widget.id ? 'fa-check' : 'fa-code'}`} style={{ fontSize: '10px' }} />
                    {copiedId === widget.id ? 'Copied' : 'Embed'}
                  </button>
                  <button onClick={() => handleDelete(widget.id)}
                    style={{ height: '32px', width: '32px', borderRadius: '8px', border: '1px solid #FCA5A5', background: '#FEF2F2', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-trash" style={{ color: '#DC2626', fontSize: '11px' }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ProtectedLayout>
  );
}
